import MessageApi from '../../../../api/inbox/message';
import types from '../../../mutation-types';
import { MESSAGE_TYPE } from 'shared/constants/messages';

export default {
  /**
   * Translate a single message
   * @param {Object} context - Vuex action context
   * @param {Object} params - Translation parameters
   * @param {number} params.conversationId - Conversation ID
   * @param {number} params.messageId - Message ID to translate
   * @param {string} params.targetLanguage - Target language code (e.g., 'en', 'es')
   * @param {string} params.provider - Translation provider ('easydesk', 'openai', 'google_cloud')
   */
  async translateMessage(
    { commit, getters },
    { conversationId, messageId, targetLanguage, provider = 'easydesk' }
  ) {
    // Check if message still exists in state before translating
    const conversation = getters.getConversationById(conversationId);
    const messageExists = conversation?.messages?.some(msg => msg.id === messageId);
    if (!messageExists) {
      return null;
    }

    // Skip if message is already being translated
    if (getters.isMessageTranslating(messageId)) {
      return null;
    }

    // Set translating state before API call
    commit(types.SET_MESSAGE_TRANSLATING, { messageId, isTranslating: true });
    try {
      const response = await MessageApi.translateMessage(
        conversationId,
        messageId,
        targetLanguage,
        provider
      );
      // Update message in store with translation data if response contains it
      if (response.data) {
        commit(
          types.UPDATE_MESSAGE_TRANSLATION,
          {
            conversationId,
            messageId,
            translation: response.data,
          }
        );
      }
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      // Clear translating state after API call completes (success or failure)
      commit(types.SET_MESSAGE_TRANSLATING, { messageId, isTranslating: false });
    }
  },

  /**
   * Translate multiple messages in batch
   * @param {Object} context - Vuex action context
   * @param {Object} params - Translation parameters
   * @param {number} params.conversationId - Conversation ID
   * @param {Array<number>} params.messageIds - Array of message IDs to translate
   * @param {string} params.targetLanguage - Target language code
   * @param {string} params.provider - Translation provider
   */
  async translateBatch(
    { commit, getters },
    { conversationId, messageIds, targetLanguage, provider = 'easydesk' }
  ) {
    // Filter out messages that are already being translated to prevent duplicate API calls
    const filteredMessageIds = messageIds.filter(
      messageId => !getters.isMessageTranslating(messageId)
    );

    // Early return if no messages need translation
    if (!filteredMessageIds.length) {
      return { translations: {} };
    }

    // Set translating state for all messages before API call
    filteredMessageIds.forEach(messageId => {
      commit(types.SET_MESSAGE_TRANSLATING, { messageId, isTranslating: true });
    });
    try {
      const response = await MessageApi.translateBatch(
        conversationId,
        filteredMessageIds,
        targetLanguage,
        provider
      );
      // Update multiple messages with their translations
      // Backend returns translations as an object: { message_id: { lang: text, ... }, ... }
      const translations = response?.data?.translations;
      if (translations && typeof translations === 'object') {
        Object.entries(translations).forEach(([messageIdStr, translationResult]) => {
          const messageId = parseInt(messageIdStr, 10);
          commit(
            types.UPDATE_MESSAGE_TRANSLATION,
            {
              conversationId,
              messageId,
              translation: translationResult,
            }
          );
        });
      }
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      // Clear translating state for all messages after API call completes (success or failure)
      filteredMessageIds.forEach(messageId => {
        commit(types.SET_MESSAGE_TRANSLATING, { messageId, isTranslating: false });
      });
    }
  },

  /**
   * Translate all messages in a conversation
   * @param {Object} context - Vuex action context
   * @param {Object} params - Translation parameters
   * @param {number} params.conversationId - Conversation ID
   * @param {string} params.targetLanguage - Target language code
   * @param {string} params.provider - Translation provider
   * @param {Array<number>} params.messageIds - Optional specific message IDs to translate
   */
  async translateConversation(
    { dispatch, getters },
    { conversationId, targetLanguage, provider = 'easydesk', messageIds = [] }
  ) {
    try {
      const conversation = getters.getConversationById(conversationId);
      const conversationMessages = conversation?.messages || [];

      // Build a set of valid message IDs (incoming messages with non-blank content)
      const validMessageIds = new Set(
        conversationMessages
          .filter(msg => msg.message_type === MESSAGE_TYPE.INCOMING && msg.content && msg.content.trim() !== '')
          .map(msg => msg.id)
      );

      let idsToTranslate;
      if (messageIds.length) {
        // Filter provided messageIds to only include valid ones (non-blank content)
        idsToTranslate = messageIds.filter(id => validMessageIds.has(id));
      } else {
        // No messageIds provided - use all valid message IDs
        idsToTranslate = Array.from(validMessageIds);
      }

      if (!idsToTranslate.length) {
        return { translations: [] };
      }

      // Use batch translation for efficiency
      return await dispatch('translateBatch', {
        conversationId,
        messageIds: idsToTranslate,
        targetLanguage,
        provider,
      });
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update conversation translation settings (auto-translate preference)
   * @param {Object} context - Vuex action context
   * @param {Object} params - Settings parameters
   * @param {number} params.conversationId - Conversation ID
   * @param {boolean} params.autoTranslate - Whether to auto-translate
   * @param {string} params.targetLanguage - Default target language
   * @param {string} params.provider - Default translation provider
   */
  async updateConversationTranslationSettings(
    { commit },
    { conversationId, autoTranslate, targetLanguage, provider }
  ) {
    try {
      // Store translation settings locally in conversation custom attributes
      // This could also be persisted to backend if API endpoint exists
      commit(
        types.SET_CONVERSATION_TRANSLATION_SETTINGS,
        {
          conversationId,
          settings: {
            autoTranslate,
            targetLanguage,
            provider,
          },
        }
      );
      return { success: true };
    } catch (error) {
      throw error;
    }
  },
};
